import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import documentControlService, {
  type ControlledDocument,
} from '@/services/documentControl.service';
import { useAuthStore } from '@/store/authStore';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const statusColor = (status: string) => {
  const map: Record<string, string> = {
    Draft: 'default',
    'Pending Approval': 'processing',
    Issued: 'success',
    Obsolete: 'error',
    Superseded: 'warning',
  };
  return map[status] || 'default';
};

const DocumentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const originTab = (searchParams.get('origin') || 'INTERNAL').toUpperCase();
  const perms = (useAuthStore((s) => s.user?.permissions) ?? []) as string[];
  const canCreate =
    perms.includes('quality.document_control.create') ||
    perms.includes('quality.document_control.edit') ||
    perms.length === 0;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [level, setLevel] = useState<string | undefined>();
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['document-control', originTab, status, level, overdueOnly],
    queryFn: () =>
      documentControlService.list({
        origin: originTab,
        status: status || undefined,
        document_level: level || undefined,
        overdue_review: overdueOnly ? '1' : undefined,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['document-control', 'dashboard-stats'],
    queryFn: () => documentControlService.dashboardStats(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.document_number.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        String(r.publisher_or_source || '')
          .toLowerCase()
          .includes(q)
    );
  }, [rows, search]);

  const columns: ColumnsType<ControlledDocument> = [
    {
      title: 'Document No.',
      dataIndex: 'document_number',
      width: 180,
      render: (v, r) => (
        <a onClick={() => navigate(`/qms/document-management/${r.id}`)}>
          {v}
          {Number(r.is_translation) ? ' (TR)' : ''}
        </a>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      ellipsis: true,
    },
    ...(originTab === 'INTERNAL'
      ? [
          {
            title: 'Level',
            dataIndex: 'document_level',
            width: 70,
            render: (v: string) => v || '—',
          } as ColumnsType<ControlledDocument>[number],
        ]
      : [
          {
            title: 'Source',
            dataIndex: 'publisher_or_source',
            width: 160,
            ellipsis: true,
            render: (v: string) => v || '—',
          } as ColumnsType<ControlledDocument>[number],
        ]),
    {
      title: 'Department',
      key: 'department',
      width: 160,
      ellipsis: true,
      render: (_, r) =>
        r.department_name
          ? `${r.finance_department_code || r.department_code || ''} — ${r.department_name}`.replace(/^ — /, '')
          : r.department_code || '—',
    },
    {
      title: 'Rev',
      dataIndex: 'revision',
      width: 70,
    },
    {
      title: 'Rev date',
      dataIndex: 'revision_date',
      width: 120,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      render: (v: string, r) => (
        <Space size={4} wrap>
          <Tag color={statusColor(v)}>{v}</Tag>
          {v === 'Issued' && Number(r.requires_controlled_stamp) ? (
            <Tag color="blue">CONTROLLED</Tag>
          ) : null}
          {Number(r.overdue_review) ? <Tag color="red">Review due</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Custodian',
      key: 'custodian',
      width: 160,
      ellipsis: true,
      render: (_, r) => r.custodian_name || r.custodian_location || '—',
    },
    {
      title: 'Next review',
      dataIndex: 'next_review_date',
      width: 120,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Document Management
          </Title>
          <Text type="secondary">
            QMS Document Control — Internal &amp; External controlled documents (API Spec Q1 / ISO 9001)
          </Text>
        </div>
        <Space wrap>
          <ReportDocumentActions
            reportKey="quality-document-control-master-list"
            params={{ origin: originTab }}
            sendPermissions={['quality.document_control.edit', 'quality.document_control.distribute']}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
          {canCreate ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/qms/document-management/new?origin=${originTab}`)
              }
            >
              New document
            </Button>
          ) : null}
        </Space>
      </Space>

      <Space size={12} style={{ marginBottom: 16 }} wrap>
        <Card size="small">
          Internal: <strong>{stats?.internal_count ?? '—'}</strong>
        </Card>
        <Card size="small">
          External: <strong>{stats?.external_count ?? '—'}</strong>
        </Card>
        <Card size="small">
          Issued: <strong>{stats?.issued_count ?? '—'}</strong>
        </Card>
        <Card size="small">
          Pending: <strong>{stats?.pending_count ?? '—'}</strong>
        </Card>
        <Card size="small">
          Overdue review: <strong>{stats?.overdue_review_count ?? '—'}</strong>
        </Card>
      </Space>

      <Tabs
        activeKey={originTab}
        onChange={(key) => {
          setSearchParams({ origin: key });
          setLevel(undefined);
        }}
        items={[
          { key: 'INTERNAL', label: 'Internal' },
          { key: 'EXTERNAL', label: 'External' },
        ]}
      />

      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          allowClear
          placeholder="Search number / title"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 180 }}
          value={status}
          onChange={setStatus}
          options={['Draft', 'Pending Approval', 'Issued', 'Obsolete', 'Superseded'].map((s) => ({
            value: s,
            label: s,
          }))}
        />
        {originTab === 'INTERNAL' ? (
          <Select
            allowClear
            placeholder="Level"
            style={{ width: 120 }}
            value={level}
            onChange={setLevel}
            options={['I', 'II', 'III', 'IV'].map((s) => ({ value: s, label: `Level ${s}` }))}
          />
        ) : (
          <Select
            value={overdueOnly ? '1' : '0'}
            style={{ width: 180 }}
            onChange={(v) => setOverdueOnly(v === '1')}
            options={[
              { value: '0', label: 'All reviews' },
              { value: '1', label: 'Overdue review' },
            ]}
          />
        )}
      </Space>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        onRow={(r) => ({
          onDoubleClick: () => navigate(`/qms/document-management/${r.id}`),
        })}
      />
    </div>
  );
};

export default DocumentManagementPage;
